import { useEffect, useState }                            from 'react';
import { Box, Card, Typography }                          from '@mui/material';
import { Node }                                           from '@xyflow/react';
import { useDispatch, useSelector }                       from 'react-redux';
import { useParams }                                      from 'react-router';
import { WorkflowAPI }                                    from 'apis/workflowAPI';
import { BglPrimaryButton, BglCloseButton }               from 'components/button/BglButtons';
import { useGlobalConfig }                                from 'context/GlobalContext';
import { ServiceNodeData }                                from './CustomNode';
import { CustomNodeConfigs }                              from './CustomNodeConfigs';
import MarkdownEditor                                     from './MarkdownEditor';
import { APIData, GetValues }                             from 'types/cms/frontendTypes';
import { updateSystemNode,
         selectNodes,
         selectSelectedWorkflowDocId
       }                                                    from 'store/dataReducers/workflowSlice';

export const CustomNodeSpecifications = () => {

  const { rc } = useGlobalConfig();
  const { id } = useParams();

  const nodes = useSelector(selectNodes)
  const selectedWorkflowDocId = useSelector(selectSelectedWorkflowDocId)

  const [node, setNode] = useState<Node<ServiceNodeData>>(nodes.find((node) => node.id === id)!);
  const [markdownContent, setMarkdownContent] = useState(node.data.specifications || '');

  const [isDirty, setIsDirty] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {

    if (!nodes) return

    const node = nodes.find((node) => node.id === id)
    if (!node) return
    
    setNode(node)
    setMarkdownContent(node.data.specifications || '');

  }, [nodes, id])

  const onUpdate = async () => {

    if (isDirty) {

      const currNode = node
      currNode.data.specifications = markdownContent

      await updateNode(currNode)
      onCancel()
    }
  }

  const updateNode = async (node : Node<ServiceNodeData>) => {

    const sysNode : GetValues<"api::system-node.system-node"> = {
      label : node.data.label,
      description : node.data.description,
      specifications : markdownContent,
      configs : node.data.configs as any,
    }

    const updatedNode = await WorkflowAPI.updateSystemNode(rc, selectedWorkflowDocId, sysNode)
    if (updatedNode.error) {
      console.log(`Could not update system node : ${updatedNode.error}`);
      return
    }

    dispatch(updateSystemNode(updatedNode.data!))
  }

  const handleSystemNodeUpdate = (node : APIData<"api::system-node.system-node">) => {
    dispatch(updateSystemNode(node))
  }

  const onCancel = () => {
    setMarkdownContent(node?.data?.specifications || '');
    setIsDirty(false);
  }

  const handleMarkdownChange = (content: string) => {
    setMarkdownContent(content);

    setIsDirty(content !== node?.data?.specifications);
  }

  return (
    <Box sx={{ p: 2, height: '100%', textAlign: 'left', overflowY: 'auto'}}>

      <Card sx={{ p: 2, display: 'flex', flexDirection: 'column'}}>
        <Typography variant="bglSubtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
          {node?.data?.label}
        </Typography>
        <Typography variant="bglBody1" sx={{ mb: 1 }}>
          {node?.data?.description}
        </Typography>  
      </Card>
        
      <CustomNodeConfigs 
        node={node}
        handleSystemNodeUpdate={handleSystemNodeUpdate}
      />

      <Box sx={{ height: 'calc(100% - 300px)', mt: 2, overflowY: 'auto' }}>
        <MarkdownEditor 
          initialContent={markdownContent}
          onChange={handleMarkdownChange}
        />
      </Box>
      <div style={{ width: '100%', padding: '20px', textAlign: 'center' }}>
        <BglCloseButton sx={{ marginRight: '10px' }} onClick={onCancel}>
            Cancel 
        </BglCloseButton>
        <BglPrimaryButton
          type="submit" disabled={!isDirty} onClick={onUpdate}>
            Save
        </BglPrimaryButton>
      </div>

    </Box>
  );
};
